import { SatelliteConversationFeatureType } from '@zg-rentals/ts-satellite-client';
import { useMemo } from 'react';

type DisabledFeature = {
  displayMessage: string;
  feature: SatelliteConversationFeatureType;
};

/**
 * Structural over the three identical per-endpoint disabled-feature DTOs (lead-management
 * row, getMessages conversation, inbox conversation) so one accessor serves all surfaces.
 */
export type LeadRestrictionsSource = {
  disabledFeatures: Array<DisabledFeature>;
};

export type LeadRestrictions = {
  canInviteToApply: boolean;
  canRequestRenterProfile: boolean;
  canScheduleTour: boolean;
  canSendMessage: boolean;
  canViewMessages: boolean;
  canViewRenterJourney: boolean;
  canViewScreeningCriteria: boolean;
  canViewTourDetails: boolean;
  getDisabledMessage: (feature: SatelliteConversationFeatureType) => string | undefined;
};

/**
 * Normalizes a row/conversation's `disabledFeatures` into UI capabilities. Consumers gate on
 * these instead of reading `disabledFeatures` directly, and render `getDisabledMessage(...)`
 * copy verbatim. A feature value this build doesn't recognize matches no capability, so new
 * server-side features fail open until the client consumes a regenerated enum.
 */
export const getLeadRestrictions = (lead: LeadRestrictionsSource | null | undefined): LeadRestrictions => {
  const disabledMessageByFeature = new Map<SatelliteConversationFeatureType, string>();
  for (const { displayMessage, feature } of lead?.disabledFeatures ?? []) {
    if (!disabledMessageByFeature.has(feature)) {
      disabledMessageByFeature.set(feature, displayMessage);
    }
  }
  const canUse = (feature: SatelliteConversationFeatureType): boolean => !disabledMessageByFeature.has(feature);
  return {
    canInviteToApply: canUse(SatelliteConversationFeatureType.INVITE_TO_APPLY),
    canRequestRenterProfile: canUse(SatelliteConversationFeatureType.REQUEST_RENTER_PROFILE),
    canScheduleTour: canUse(SatelliteConversationFeatureType.SCHEDULE_TOUR),
    canSendMessage: canUse(SatelliteConversationFeatureType.SEND_MESSAGE),
    canViewMessages: canUse(SatelliteConversationFeatureType.VIEW_MESSAGES),
    canViewRenterJourney: canUse(SatelliteConversationFeatureType.VIEW_RENTER_JOURNEY),
    canViewScreeningCriteria: canUse(SatelliteConversationFeatureType.VIEW_SCREENING_CRITERIA),
    canViewTourDetails: canUse(SatelliteConversationFeatureType.VIEW_TOUR_DETAILS),
    getDisabledMessage: (feature) => disabledMessageByFeature.get(feature),
  };
};

const useLeadRestrictions = (lead: LeadRestrictionsSource | null | undefined): LeadRestrictions =>
  useMemo(() => getLeadRestrictions(lead), [lead]);

export default useLeadRestrictions;
